import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Environment } from "../../../types";
import type { TokenInfoResponse } from "../types";
import { getApiBase } from "../../../lib/remote-config";

export const TOKEN_INFO_QUERY_KEY = "token-info";

function buildCategoryKey(chainId: number, address: string): string {
  return `${address.toLowerCase()}_${chainId}`;
}

export function useTokenInfo(env: Environment, enabled = true) {
  const { data, ...rest } = useQuery<TokenInfoResponse>({
    queryKey: [TOKEN_INFO_QUERY_KEY, env],
    queryFn: async ({ signal }) => {
      const baseUrl = await getApiBase("walletServer", env);
      const res = await fetch(`${baseUrl}/v1/public/token/info`, {
        cache: "no-store",
        signal,
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch token info: ${res.status}`);
      }
      const json: TokenInfoResponse = await res.json();
      if (json.code !== 200) {
        throw new Error(`Token info API error: ${json.code}`);
      }
      return json;
    },
    enabled,
    staleTime: Infinity,
  });

  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!data?.data) return map;
    for (const info of data.data) {
      const key = buildCategoryKey(info.chain_id, info.address);
      map.set(key, info.category);
    }
    return map;
  }, [data]);

  return { categoryMap, ...rest };
}

export { buildCategoryKey };
